import sparkle from '@/assets/svgs/icons/sparkle.svg';
import { db } from '@/Firebase';
import { classNames, emptyConverter } from '@/utils';
import { Website } from '@superfunnel/types';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { ArrowRight, NavArrowDown, NavArrowUp, Search } from 'iconoir-react';
import { useCallback, useMemo, useState } from 'react';
import { DynamicPageWithVariants, VariantSelection } from './types';

interface WebsiteInfo {
  id: string;
  name?: string;
  description?: string;
  status: string;
}

interface DynamicPageListProps {
  dynamicPages: DynamicPageWithVariants[];
  selectedVariant?: VariantSelection;
  onSelect: (selection: VariantSelection) => void;
  accountId: string;
}

export function DynamicPageList({
  dynamicPages,
  selectedVariant,
  onSelect,
  accountId,
}: DynamicPageListProps) {
  const [expandedPageId, setExpandedPageId] = useState<string | null>(null);
  const [expandedVariantId, setExpandedVariantId] = useState<string | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredVariantId, setHoveredVariantId] = useState<string | null>(null);
  // Cache for loaded website names: Map<dynamicPageId, Map<websiteId, WebsiteInfo>>
  const [websitesCache, setWebsitesCache] = useState<
    Map<string, Map<string, WebsiteInfo>>
  >(new Map());
  const [loadingWebsites, setLoadingWebsites] = useState<Set<string>>(
    new Set(),
  );

  const fetchWebsitesForPage = useCallback(
    async (dynamicPageId: string) => {
      if (
        websitesCache.has(dynamicPageId) ||
        loadingWebsites.has(dynamicPageId)
      ) {
        return;
      }

      setLoadingWebsites((prev) => new Set(prev).add(dynamicPageId));

      try {
        const websitesColRef = collection(
          db,
          'accounts',
          accountId,
          'campaigns',
          dynamicPageId,
          'websites',
        ).withConverter(emptyConverter<Website>());

        const websitesSnapshot = await getDocs(
          query(
            websitesColRef,
            where('status', 'in', ['draft', 'active', 'disabled', 'creating']),
            orderBy('createdAt', 'asc'),
          ),
        );

        const websitesMap = new Map<string, WebsiteInfo>();
        websitesSnapshot.docs.forEach((wsDoc) => {
          const websiteData = wsDoc.data();
          websitesMap.set(wsDoc.id, {
            id: wsDoc.id,
            // Only store the name if it exists
            name: websiteData.name || '',
            description: websiteData.description || '',
            status: websiteData.status,
          });
        });

        setWebsitesCache((prev) =>
          new Map(prev).set(dynamicPageId, websitesMap),
        );
      } catch (err) {
        console.error(
          `Failed to fetch websites for page ${dynamicPageId}:`,
          err,
        );
      } finally {
        setLoadingWebsites((prev) => {
          const next = new Set(prev);
          next.delete(dynamicPageId);
          return next;
        });
      }
    },
    [accountId, websitesCache, loadingWebsites],
  );

  const filteredPages = useMemo(() => {
    let pages = dynamicPages;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      pages = pages.filter((dp) => {
        const dpName = dp.dynamicPage.data().name.toLowerCase();
        const hasMatchingVariant = dp.variants.some((v) =>
          v.data().name.toLowerCase().includes(query),
        );
        return dpName.includes(query) || hasMatchingVariant;
      });
    }

    return pages;
  }, [dynamicPages, searchQuery]);

  const toggleExpandPage = (pageId: string) => {
    setExpandedPageId((prev) => (prev === pageId ? null : pageId));
    // Reset variant expansion when switching pages
    setExpandedVariantId(null);
  };

  const toggleExpandVariant = (variantId: string, dynamicPageId: string) => {
    const isExpanding = expandedVariantId !== variantId;
    setExpandedVariantId((prev) => (prev === variantId ? null : variantId));

    // Fetch websites when expanding a variant
    if (isExpanding) {
      fetchWebsitesForPage(dynamicPageId);
    }
  };

  const getExperimentLabel = (index: number): string => {
    if (index === 0) return 'Control';
    // Convert index to letter: 1 -> A, 2 -> B, etc.
    const letter = String.fromCharCode(64 + index); // 65 is 'A'
    return `Experiment ${letter}`;
  };

  const getWebsiteName = (
    dynamicPageId: string,
    websiteId: string,
    index: number,
  ): string | null => {
    const pageCache = websitesCache.get(dynamicPageId);
    if (pageCache) {
      const websiteInfo = pageCache.get(websiteId);
      if (websiteInfo && websiteInfo.name) {
        return websiteInfo.name;
      }
      // Website found but has no name - use experiment label
      if (websiteInfo) {
        return getExperimentLabel(index);
      }
    }
    // Return null to indicate loading state
    if (loadingWebsites.has(dynamicPageId)) {
      return null;
    }
    return getExperimentLabel(index);
  };

  const isWebsiteDraft = (
    dynamicPageId: string,
    websiteId: string,
  ): boolean => {
    const pageCache = websitesCache.get(dynamicPageId);
    if (pageCache) {
      const websiteInfo = pageCache.get(websiteId);
      if (websiteInfo) {
        return websiteInfo.status === 'draft';
      }
    }
    return false;
  };

  if (filteredPages.length === 0 && !searchQuery) {
    return (
      <div className="text-base-500 py-4 text-center text-sm">
        No other dynamic pages available
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-1.5">
      {/* Search input */}
      <div className="flex flex-col gap-1.5 rounded-xl border border-[#e4e7ec] bg-white px-1 py-1.5">
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-3">
            <Search className="text-base-500 size-[18px]" strokeWidth={1.2} />
            <input
              type="text"
              placeholder="Search variant..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="text-base-900 placeholder:text-base-500 border-none bg-transparent text-sm focus:ring-0 focus:outline-none"
            />
          </div>
        </div>

        {/* Separator */}
        <div className="px-2">
          <div className="h-px w-full bg-[#e4e7ec]" />
        </div>

        {/* Dynamic pages list */}
        <div className="max-h-64 overflow-y-auto">
          {filteredPages.length === 0 ? (
            <div className="text-base-500 py-4 text-center text-sm">
              No results found
            </div>
          ) : (
            filteredPages.map((dp, index) => {
              const pageData = dp.dynamicPage.data();
              const isPageExpanded = expandedPageId === dp.dynamicPage.id;

              return (
                <div key={dp.dynamicPage.id}>
                  {/* Separator between items */}
                  {index > 0 && (
                    <div className="px-2">
                      <div className="h-px w-full bg-[#f2f4f7]" />
                    </div>
                  )}

                  {/* Dynamic page header */}
                  <div className="rounded-xl bg-white px-2 py-1.5">
                    <button
                      type="button"
                      onClick={() => toggleExpandPage(dp.dynamicPage.id)}
                      className="flex h-[30px] w-full items-center justify-between px-1.5"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-base-900 text-xs font-semibold">
                          {pageData.name}
                        </span>
                      </div>
                      <div className="flex size-8 items-center justify-center rounded-lg">
                        {isPageExpanded ? (
                          <NavArrowUp
                            className="text-base-500 size-[18px]"
                            strokeWidth={1.5}
                          />
                        ) : (
                          <NavArrowDown
                            className="text-base-500 size-[18px]"
                            strokeWidth={1.5}
                          />
                        )}
                      </div>
                    </button>

                    {/* Variants list (expandable) */}
                    {isPageExpanded && (
                      <div className="flex flex-col">
                        {dp.variants.length === 0 ? (
                          <div className="text-base-500 py-2 text-center text-xs">
                            No variants available
                          </div>
                        ) : (
                          dp.variants.map((variant) => {
                            const variantData = variant.data();
                            const websiteIds = variantData.websiteIds || [];
                            const hasExperiment = websiteIds.length > 1;
                            const isVariantExpanded =
                              expandedVariantId === variant.id;
                            const isVariantSelected =
                              selectedVariant?.variantId === variant.id &&
                              selectedVariant?.dynamicPageId ===
                                dp.dynamicPage.id;
                            const isHovered = hoveredVariantId === variant.id;

                            // For variants with experiments, show expandable list
                            if (hasExperiment) {
                              return (
                                <div key={variant.id} className="flex flex-col">
                                  {/* Variant header with expand/collapse */}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      toggleExpandVariant(
                                        variant.id,
                                        dp.dynamicPage.id,
                                      )
                                    }
                                    onMouseEnter={() =>
                                      setHoveredVariantId(variant.id)
                                    }
                                    onMouseLeave={() =>
                                      setHoveredVariantId(null)
                                    }
                                    className={classNames(
                                      'relative flex items-center justify-between rounded-lg p-1.5 transition-colors',
                                      isVariantSelected
                                        ? 'bg-base-100'
                                        : 'hover:bg-base-50',
                                    )}
                                  >
                                    <div className="flex items-center gap-[5px]">
                                      <div className="flex w-3 items-center justify-center">
                                        <img
                                          src={sparkle}
                                          alt=""
                                          className="size-2.5"
                                        />
                                      </div>
                                      <span className="text-base-900 text-xs">
                                        {variantData.name}
                                      </span>
                                      <span className="text-base-500 ml-1 text-[10px]">
                                        ({websiteIds.length} tests)
                                      </span>
                                    </div>
                                    <div className="flex size-5 items-center justify-center rounded-lg">
                                      {isVariantExpanded ? (
                                        <NavArrowUp
                                          className="text-base-500 size-3.5"
                                          strokeWidth={1.5}
                                        />
                                      ) : (
                                        <NavArrowDown
                                          className="text-base-500 size-3.5"
                                          strokeWidth={1.5}
                                        />
                                      )}
                                    </div>
                                  </button>

                                  {/* Expandable test list */}
                                  {isVariantExpanded && (
                                    <div className="ml-4 flex flex-col gap-0.5 border-l border-[#e4e7ec] pl-2">
                                      {websiteIds.map((websiteId, wsIndex) => {
                                        const isTestSelected =
                                          selectedVariant?.websiteId ===
                                            websiteId &&
                                          selectedVariant?.variantId ===
                                            variant.id;
                                        const dynamicPageId = dp.dynamicPage.id;
                                        const testName = getWebsiteName(
                                          dynamicPageId,
                                          websiteId,
                                          wsIndex,
                                        );
                                        const isDraft = isWebsiteDraft(
                                          dynamicPageId,
                                          websiteId,
                                        );

                                        const buttonContent = (
                                          <button
                                            key={websiteId}
                                            type="button"
                                            disabled={isDraft}
                                            onClick={() =>
                                              onSelect({
                                                variantId: variant.id,
                                                variantName: variantData.name,
                                                websiteId,
                                                dynamicPageId:
                                                  dp.dynamicPage.id,
                                                dynamicPageName: pageData.name,
                                              })
                                            }
                                            className={classNames(
                                              'relative flex w-full items-center justify-between rounded-md px-2 py-1 transition-colors',
                                              isDraft &&
                                                'cursor-not-allowed opacity-50',
                                              isTestSelected
                                                ? 'bg-base-100'
                                                : !isDraft &&
                                                    'hover:bg-base-50',
                                            )}
                                          >
                                            {testName === null ? (
                                              <div className="bg-base-200 h-3 w-20 animate-pulse rounded" />
                                            ) : (
                                              <span className="text-base-700 text-[10px]">
                                                {testName}
                                              </span>
                                            )}
                                            <div
                                              className={classNames(
                                                'flex items-center gap-1 transition-opacity',
                                                isTestSelected
                                                  ? 'opacity-100'
                                                  : 'opacity-0',
                                              )}
                                            >
                                              <ArrowRight
                                                className="text-brand-900 size-2.5"
                                                strokeWidth={1}
                                              />
                                            </div>
                                          </button>
                                        );

                                        if (isDraft) {
                                          return (
                                            <Tooltip key={websiteId}>
                                              <TooltipTrigger asChild>
                                                {buttonContent}
                                              </TooltipTrigger>
                                              <TooltipContent
                                                side="left"
                                                sideOffset={4}
                                              >
                                                This is a draft test and cannot
                                                be selected
                                              </TooltipContent>
                                            </Tooltip>
                                          );
                                        }

                                        return buttonContent;
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            // For variants without experiments, show simple row
                            const websiteId = websiteIds[0] || '';

                            return (
                              <button
                                key={variant.id}
                                type="button"
                                onClick={() =>
                                  onSelect({
                                    variantId: variant.id,
                                    variantName: variantData.name,
                                    websiteId,
                                    dynamicPageId: dp.dynamicPage.id,
                                    dynamicPageName: pageData.name,
                                  })
                                }
                                onMouseEnter={() =>
                                  setHoveredVariantId(variant.id)
                                }
                                onMouseLeave={() => setHoveredVariantId(null)}
                                className={classNames(
                                  'relative flex items-center justify-between rounded-lg p-1.5 transition-colors',
                                  isVariantSelected
                                    ? 'bg-base-100'
                                    : 'hover:bg-base-50',
                                )}
                              >
                                <div className="flex items-center gap-[5px]">
                                  <div className="flex w-3 items-center justify-center">
                                    <img
                                      src={sparkle}
                                      alt=""
                                      className="size-2.5"
                                    />
                                  </div>
                                  <span className="text-base-900 text-xs">
                                    {variantData.name}
                                  </span>
                                </div>
                                <div
                                  className={classNames(
                                    'flex items-center gap-1 rounded-lg transition-opacity',
                                    isHovered || isVariantSelected
                                      ? 'opacity-100'
                                      : 'opacity-0',
                                  )}
                                >
                                  <span className="text-base-900 text-xs">
                                    Select
                                  </span>
                                  <ArrowRight
                                    className="text-brand-900 size-4"
                                    strokeWidth={1}
                                  />
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
